<template>
  <v-row no-gutters class="fill-height flex-column" style="overflow-y: auto; overflow-x: hidden;">
    <template v-if="isFetched">
      <v-col cols="12">
        <card-title-row :straight-title="additionalModuleName" class="pt-6 pb-2" />
        <card-title-row
          custom-size="text-body-3 font-weight-medium"
          translation-path="sidebar.zdmEditor.editingSession"
        >
          <template #buttons>
            <base-rounded-button
              icon="mdi-plus"
              size="x-small"
              color="success"
              :disabled="!isSessionFetched"
              :tooltip="$i18n.t('sidebar.zdmEditor.addSession')"
              @click="action('addSession')"
            />
          </template>
        </card-title-row>
        <sidebar-row class="pt-2" border-bottom>
          <v-row no-gutters class="pr-1 pb-2 border">
            <v-col cols="5">
              <data-input
                v-model="currentSessionId"
                :items="sessionsArray"
                item-text="name"
                item-value="id"
                :data-type="{ name: 'select' }"
                :disabled="!isSessionFetched"
                :loading="!isSessionFetched"
              />
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="auto" class="pt-2">
              <dots-menu
                v-if="session && isSessionFetched"
                :items="sessionsDotsMenuItems"
                v-on="sessionsDotsMenuItemsListeners"
              ></dots-menu>
            </v-col>
          </v-row>
        </sidebar-row>
        <template v-if="isSessionFetched && session">
          <card-title-row
            custom-size="text-body-3 font-weight-medium pt-5"
            translation-path="sidebar.zdmEditor.dxfFile"
          />
          <sidebar-row border-bottom v-if="session.dxf_layers && session.dxf_layers.length">
            <v-row no-gutters class="pb-5" align="center">
              <v-col cols="auto" class="text-body-2">
                {{ sessionDxfName }}
              </v-col>
              <v-spacer />
              <v-col cols="auto">
                <v-tooltip color="font" bottom>
                  <template #activator="{ on }">
                    <v-icon v-on="on" color="rgba(0,0,0,.7)" size="19px" @click="zoomToExtent(session.dxf_layers[0])">
                      mdi-magnify
                    </v-icon>
                  </template>
                  {{ $i18n.t('navbar.zoomIn') }}
                </v-tooltip>
              </v-col>
              <v-col cols="auto">
                <dots-menu v-if="isSessionFetched" :items="dxfDotsMenuItems" v-on="dxfDotsMenuItemsListeners" />
              </v-col>
            </v-row>
          </sidebar-row>
          <sidebar-row border-bottom v-else>
            <v-row no-gutters class="pb-5">
              <v-col cols="auto">
                <base-button translation-path="sidebar.importDxf.importFile" @click="action('importDxf')"></base-button>
              </v-col>
            </v-row>
          </sidebar-row>
          <zdm-editor-features-tabs :tabs="tabs" :validation="currentSessionValidation" :errors-count="errorsCount" />
        </template>
      </v-col>
      <main-dialog
        v-if="isMainDialogLoaded"
        :is-visible.sync="isMainDialogVisible"
        v-bind="mainDialogBind"
        @agree="actionHandler"
      />
      <main-input-dialog
        v-if="isMainInputDialogLoaded"
        :is-visible.sync="isMainInputDialogVisible"
        v-bind="mainInputDialogBind"
        @agree="actionHandler"
      >
        <template #file.input>
          <v-col cols="6">
            <file-input
              ref="dxfFile"
              :options="dropzoneOptions"
              :show-added-files="true"
              :expanded-files-extensions="filesExtensions"
              :events="{
                'vdropzone-sending-multiple': sendingEvent,
                'vdropzone-success-multiple': onUploadSuccess,
              }"
            />
          </v-col>
        </template>
      </main-input-dialog>
    </template>
    <main-loader v-else />
  </v-row>
</template>
<script>
import { debounce } from 'lodash';
import { call, get, sync } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import FileInput from '@/components/FileInput';
import MainDialog from '@/components/MainDialog';
import MainInputDialog from '@/components/MainInputDialog';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';
import ZdmEditorFeaturesTabs from '@/components/ZdmEditorFeaturesTabs';

export default {
  name: 'ZDMEditor',
  components: {
    CardTitleRow,
    DataInput,
    DotsMenu,
    FileInput,
    MainDialog,
    MainInputDialog,
    MainLoader,
    SidebarRow,
    ZdmEditorFeaturesTabs,
  },
  computed: {
    featureLayerNameMaxLength: get('admin/settingsValues@feature_layer_name_max_length'),
    additionalModuleName: get('admin/additionalModules.ZDM_SESSIONS.verbose_name'),
    dataSourcesFeatures: get('layers/dataSourcesFeatures'),
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    zdmDataDataSources: get('admin/modulesMapping@zdm_data.datasources'),
    zdmDataLayers: get('admin/modulesMapping@zdm_data.layers'),
    tabsLayers() {
      return this.tabs.map(tab => this.zdmDataLayers[tab.value]);
    },
    tabsDataSources() {
      return this.tabs.map(tab => this.zdmDataDataSources[tab.value].datasource);
    },

    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },

    layers: get('layers/layers'),
    layersArray() {
      return this.$_objectToArray(this.layers);
    },
    dxfLayers() {
      return this.layersArray.filter(layer => layer.layer_scope === 'dxf');
    },
    dxfDotsMenuItems() {
      return [
        {
          name: 'deleteDxf',
        },
      ];
    },
    dxfDotsMenuItemsListeners() {
      const listeners = {};
      const actions = this.dxfDotsMenuItems.map(item => item.name);
      for (const action of actions) {
        listeners[action] = () => {
          this['action'](action, JSON.parse(JSON.stringify({ name: this.sessionDxfName })));
        };
      }
      return listeners;
    },

    currentSessionId: sync('zdm_editor/currentSessionId'),
    session: sync('zdm_editor/session'),
    sessionObjects() {
      return this.session?.session_objects || {};
    },
    sessions: sync('zdm_editor/sessions'),
    sessionsArray() {
      return this.$_sortObjectsByAttribute([...this.$_objectToArray(this.sessions)], 'name');
    },
    sessionsDotsMenuItems() {
      return [
        {
          name: 'editSessionName',
        },
        {
          name: 'deleteSession',
        },
      ];
    },
    sessionsDotsMenuItemsListeners() {
      const listeners = {};
      const actions = this.sessionsDotsMenuItems.map(item => item.name);
      for (const action of actions) {
        listeners[action] = () => {
          this['action'](action, JSON.parse(JSON.stringify(this.session)));
        };
      }
      return listeners;
    },
    sessionDxfName() {
      const layerId = this.session?.dxf_layers?.[0];
      if (layerId || layerId === 0) {
        return this.layers[layerId]?.name.replace(new RegExp('_dxf' + '$'), '');
      }
      return '';
    },

    capitalizeActionName() {
      return this.$_capitalize(this.currentAction?.name);
    },
    dropzoneOptions() {
      const self = this;
      return {
        url: `${process.env.VUE_APP_API_URL}/zdm_data/upload/dxf`,
        method: 'post',
        autoProcessQueue: false,
        acceptedFiles: '.dxf',
        uploadMultiple: true,
        maxFiles: 1,
        parallelUploads: 1,
        paramName(i) {
          const files = self.$refs.dxfFile?.$refs.dropzone?.getAcceptedFiles();
          const file = files[i];
          return file.name.split('.').pop();
        },
        accept: (file, done) => {
          const index = this.mainInputDialogItems.findIndex(input => input.name === 'dxfName');
          if (index >= 0 && !this.mainInputDialogItems[index].value) {
            this.$set(this.mainInputDialogItems, index, {
              ...this.mainInputDialogItems[index],
              value: file.name.slice(0, -4) || '',
            });
          }
          this.$forceUpdate();
          done();
        },
      };
    },
    mainDialogBind() {
      return {
        loading: this.isMainDialogLoading,
        ...(this.isMainDialogVisible
          ? {
              title: this.$i18n.t(this.currentAction.titleTranslation),
              body: this.$i18n.t(this.currentAction.bodyTranslation, {
                value: this.currentActionData[this.currentAction.bodyTranslationValueKey],
              }),
              agreeText: this.currentAction.agreeTranslation,
            }
          : {}),
      };
    },
    mainInputDialogBind() {
      return {
        loading: this.isMainInputDialogLoading,
        ...(this.isMainInputDialogVisible
          ? {
              titleTranslation: this.$i18n.t(this.currentAction.titleTranslation),
              agreeText: this.currentAction.agreeTranslation,
              inputs: this.mainInputDialogItems,
            }
          : {}),
      };
    },
    mainInputDialogItems() {
      const mainInputDialogItemsDict = {
        addSession: [
          {
            name: 'name',
            translationPath: 'dialog.body.name',
            cols: '12',
            rules: [v => !!v || v == '0' || 'rules.required'],
            dataType: {
              name: 'text',
            },
          },
        ],
        editSessionName: [
          {
            name: 'name',
            translationPath: 'dialog.body.name',
            cols: '12',
            rules: [v => !!v || v == '0' || 'rules.required'],
            dataType: {
              name: 'text',
            },
          },
        ],
        importDxf: [
          {
            name: 'dxfName',
            value: '',
            translationPath: 'dialog.name',
            cols: '12',
            rules: [v => !!v || v == '0' || 'rules.required'],
            dataType: {
              name: 'text',
              max_length: this.featureLayerNameMaxLength,
            },
          },
          {
            name: 'file',
            translationPath: 'dialog.chooseDxfFile',
            align: 'start',
            isCustom: true,
          },
        ],
      };
      return mainInputDialogItemsDict[this.currentAction?.name]?.map(input => {
        return { ...input, value: JSON.parse(JSON.stringify(this.currentActionData?.[input.name] || '')) };
      });
    },

    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
  },
  data: () => ({
    currentSessionValidation: undefined,
    errorsCount: {},
    filesExtensions: [
      {
        name: 'dxf',
        url: `${process.env.VUE_APP_API_URL}/zdm_data/upload/dxf`,
        exts: ['dxf'],
        canExtDuplicate: false,
        minFiles: 1,
        maxFiles: 1,
      },
    ],
    tabs: [
      {
        value: 'street_lamp',
      },
      {
        value: 'cable',
      },
      {
        value: 'cabinet',
      },
      {
        value: 'power_station',
      },
    ],

    isFetched: false,
    isSessionFetched: false,

    actionDialogsDict: {
      addSession: {
        name: 'addSession',
        dialog: 'MainInputDialog',
        agreeTranslation: 'dialog.agreeAdd',
        titleTranslation: 'dialog.title.addSession',
      },
      deleteDxf: {
        name: 'deleteDxf',
        dialog: 'MainDialog',
        agreeTranslation: 'dialog.agreeDelete',
        bodyTranslation: 'dialog.body.confirmDeleteDxf',
        bodyTranslationValueKey: 'name',
        titleTranslation: 'dialog.title.deleteDxf',
      },
      deleteSession: {
        name: 'deleteSession',
        dialog: 'MainDialog',
        agreeTranslation: 'dialog.agreeDelete',
        bodyTranslation: 'dialog.body.confirmDeleteSession',
        bodyTranslationValueKey: 'name',
        titleTranslation: 'dialog.title.deleteSession',
      },
      editSessionName: {
        name: 'editSessionName',
        dialog: 'MainInputDialog',
        agreeTranslation: 'dialog.agreeSave',
        titleTranslation: 'dialog.title.editSessionName',
      },
      importDxf: {
        name: 'importDxf',
        dialog: 'MainInputDialog',
        agreeTranslation: 'dialog.body.import',
        titleTranslation: 'dialog.importDxfFile',
        customResponseAction: true,
      },
    },
    currentAction: undefined,
    currentActionData: undefined,
    isMainDialogLoaded: false,
    isMainDialogLoading: false,
    isMainDialogVisible: false,
    isMainInputDialogLoaded: false,
    isMainInputDialogLoading: false,
    isMainInputDialogVisible: false,
  }),
  methods: {
    deleteDataSource: call('layers/deleteDataSource'),
    deleteLayerAction: call('layers/deleteLayerWithouUpdate'),
    getCurrentUser: call('users/getCurrentUser'),
    getLayers: call('layers/getLayers'),
    getDataSources: call('layers/getLayersMetadata'),
    getLayersSchema: call('layers/getLayersSchema'),
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    addSession: call('zdm_editor/addSession'),
    deleteSession: call('zdm_editor/deleteSession'),
    editSession: call('zdm_editor/editSession'),
    getSession: call('zdm_editor/getSession'),
    getSessions: call('zdm_editor/getSessions'),
    mergeSession: call('zdm_editor/mergeSession'),
    validateSession: call('zdm_editor/validateSession'),
    getLayerFeatures: call('layers/getLayerFeatures'),
    action(action, data = undefined) {
      this.currentAction = this.actionDialogsDict[action];
      this.currentActionData = data;
      const dialogType = this.actionDialogsDict[action].dialog;
      this[`is${dialogType}Loaded`] = true;
      this[`is${dialogType}Visible`] = true;
    },
    async actionHandler() {
      const dialogType = this.currentAction.dialog;
      this[`is${dialogType}Loading`] = true;
      try {
        await this[`handler${this.capitalizeActionName}`]();
        if (this.currentAction.customResponseAction) {
          return;
        }
        this[`is${dialogType}Visible`] = false;
        this[`is${dialogType}Loading`] = false;
      } catch {
        this[`is${dialogType}Loading`] = false;
      }
    },
    async handlerAddSession() {
      const session = this.$_arrayToObject(this.mainInputDialogItems, 'name', 'value');
      try {
        const r = await this.addSession(session);
        this.currentSessionId = r;
      } catch {
        //
      }
    },
    async handlerDeleteDxf() {
      const { dxf_layers } = this.session;
      const dxf_data_sources = dxf_layers.map(layerId => this.layers[layerId].data_source_name);
      let promises = dxf_layers.map(layerId => {
        return this.deleteLayerAction({ layer_id: layerId, layer_type: 'features_layers', scope: 'dxf' });
      });
      promises = promises.concat(
        dxf_data_sources.map(dataSourceName => {
          return this.deleteDataSource(dataSourceName);
        })
      );
      dxf_layers.forEach(layerId => {
        this.$store.set('layers/DELETE_PROJECT_LAYER!', layerId);
        this.$root.$emit('deleteProjectLayer', layerId);
      });
      try {
        await Promise.all(promises);
        await this.getSessions();
        await this.getSession({ id: this.currentSessionId, update: true });
      } catch {
        //
      }
    },
    async handlerDeleteSession() {
      if (this.session.dxf_layers && this.session.dxf_layers.length > 0) {
        const { dxf_layers } = this.session;
        try {
          dxf_layers.forEach(layerId => {
            this.$store.set('layers/DELETE_PROJECT_LAYER!', layerId);
            this.$root.$emit('deleteProjectLayer', layerId);
          });
        } catch {
          //
        }
      }
      try {
        await this.deleteSession(this.session.id);
        const newCurrentSessionId = this.sessionsArray[0]?.id;
        this.currentSessionId = newCurrentSessionId || newCurrentSessionId === 0 ? newCurrentSessionId : null;
      } catch {
        //
      }
    },
    async handlerEditSessionName() {
      const payload = this.$_arrayToObject(this.mainInputDialogItems, 'name', 'value');
      try {
        await this.editSession({ id: this.session.id, payload });
        this.$store.set('zdm_editor/SET_SESSION_NAME!', { id: this.session.id, name: payload.name, withCurrent: true });
      } catch {
        //
      }
    },
    async handlerImportDxf() {
      if (this.$refs.dxfFile?.$refs.dropzone?.getAcceptedFiles().length < 1) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.fileRequired'),
        });
        this[`is${this.currentAction.dialog}Loading`] = false;
      } else {
        this.$refs.dxfFile.$refs.dropzone.processQueue();
      }
    },
    async getFeatures(datasource) {
      if (!this.dataSourcesFeatures[datasource]) {
        await this.getDataSourceFeatures({
          dataSource: datasource,
        });
      }
    },
    async init() {
      if (!this.mapPanelDataFetched && !this.isFetched) {
        return;
      }
      try {
        await Promise.all([
          this.getFeatures('zdm_data_catalog_pole'),
          this.getFeatures('zdm_data_catalog_pick_arm'),
          this.getFeatures('zdm_data_catalog_head'),
          this.getFeatures('zdm_data_catalog_cable'),
          this.getSessions(),
        ]);
        if (this.sessionsArray.length > 0) {
          this.currentSessionId = this.sessionsArray[0].id;
        } else {
          this.isSessionFetched = true;
        }
        this.tabs.forEach(tab => {
          const { value } = tab;
          const layerId = this.zdmDataLayers[value];
          const attribute = this.zdmDataDataSources[value]['display_attribute_name'];
          this.$root.$emit('setProjectLayerStyle', layerId, {
            selectedColors: { labelAttributesName: [attribute] },
          });
        });
        this.isFetched = true;
      } catch {
        //
      }
    },
    async clearSessionHandler(value = this.currentSessionId) {
      const session = this.sessions[value];
      if (session?.dxf_layers?.length > 0) {
        session.dxf_layers.forEach(layerId => {
          if (this.projectLayers.find(layer => layer.id === layerId)) {
            this.$store.set('layers/DELETE_PROJECT_LAYER!', layerId);
            this.$root.$emit('deleteProjectLayer', layerId);
          }
        });
      }
    },
    async getSessionHandler(value = this.currentSessionId) {
      this.isSessionFetched = false;
      try {
        await this.getSession({ id: value, dataSources: this.tabsDataSources });
        const layersToProject = this.session.dxf_layers.map(layerId => {
          const layerToProject = this.dxfLayers.find(layer => layer.id == layerId);
          return {
            ...layerToProject,
            ...(layerToProject.geometry_type === 'point' ? { labels_visible: true } : {}),
          };
        });
        const layersNotInProject = layersToProject.filter(layer => {
          return !this.projectLayers.find(projectLayer => layer.id === projectLayer.id);
        });
        this.$root.$emit('addLayersHandler', layersNotInProject);
        this.isSessionFetched = true;
      } catch {
        this.isSessionFetched = true;
      }
    },
    async onUploadSuccess() {
      await Promise.all([
        this.getLayersSchema(),
        this.getDataSources({ with_attributes_schema: true }),
        this.getLayers({ scope: 'dxf' }),
        this.getCurrentUser(),
        this.getSessions(),
      ]);
      await this.getSession({ id: this.currentSessionId, update: true });
      const dxf_layers = this.session.dxf_layers;
      const layersToProject = dxf_layers.map(layerId => {
        const layerToProject = this.dxfLayers.find(layer => layer.id == layerId);
        return {
          ...layerToProject,
          ...(layerToProject.geometry_type === 'point' ? { labels_visible: true } : {}),
        };
      });
      const layersNotInProject = layersToProject.filter(layer => {
        return !this.projectLayers.find(projectLayer => layer.id === projectLayer.id);
      });
      this.$root.$emit('addLayersHandler', layersNotInProject);
      const dialogType = this.currentAction.dialog;
      this[`is${dialogType}Visible`] = false;
      this[`is${dialogType}Loading`] = false;
    },
    sendingEvent(files, xhr, formData) {
      formData.append('datasource_name', this.mainInputDialogItems.find(aDSI => aDSI.name === 'dxfName').value);
      formData.append('srid', 2178);
      formData.append('session_id', this.currentSessionId);
    },
    async agreeChanges() {
      try {
        this.isStickyButtonsLoading = true;
        await this.mergeSession(this.currentSessionId);
        this.tabsLayers.forEach(layer => this.$root.$emit('refreshMvtSource', layer));
        const newCurrentSessionId = this.sessionsArray[0]?.id;
        this.currentSessionId = newCurrentSessionId || newCurrentSessionId === 0 ? newCurrentSessionId : null;
        this.isStickyButtonsValid = true;
        this.isStickyButtonsLoading = false;
      } catch {
        this.isStickyButtonsLoading = false;
      }
    },
    async checkChanges() {
      try {
        this.isStickyButtonsLoading = true;
        const r = await this.validateSession(this.currentSessionId);
        this.currentSessionValidation = r.data.data;
        const { cables, deleted, duplicates, modified, numbers } = r.data.data;
        if (
          cables?.length ||
          deleted?.length ||
          duplicates?.length ||
          Object.keys(modified)?.length ||
          numbers?.length
        ) {
          const errorsFeaturesIds = Array.from(
            new Set([
              ...(cables || []),
              ...(deleted || []),
              ...(duplicates || []),
              ...(Object.keys(modified) || []),
              ...(numbers || []),
            ])
          );
          const errorsCount = {};
          this.tabs.forEach(tab => {
            const { value } = tab;
            const dataSourceName = this.zdmDataDataSources[value].datasource;
            const features = this.session.session_objects[dataSourceName];
            const featuresIds = [
              ...(features?.delete || []).map(feature => feature.metadata.id),
              ...(features?.insert || []).map(feature => feature.metadata.id),
            ];
            errorsCount[value] = featuresIds.filter(feature => errorsFeaturesIds.includes(feature)).length;
            this.errorsCount = errorsCount;
          });
          this.isStickyButtonsValid = false;
        } else {
          const errorsCount = {};
          this.tabs.forEach(tab => {
            const { value } = tab;
            errorsCount[value] = 0;
          });
          this.errorsCount = errorsCount;
          this.isStickyButtonsValid = true;
        }
        this.isStickyButtonsLoading = false;
      } catch {
        this.isStickyButtonsLoading = false;
      }
    },
    async zoomToExtent(id) {
      const r = await this.getLayerFeatures({
        layer_id: id,
        params: {
          with_features: false,
          with_collection_bbox: true,
          with_count: false,
          with_geometry: false,
        },
      });
      if (r.data.data.bbox) {
        this.$root.$emit('fitView', r.data.data.bbox);
      } else {
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('snackbar.zoomToEmptyLayer'),
        });
      }
    },
  },
  watch: {
    mapPanelDataFetched: {
      immediate: true,
      handler(nV) {
        if (nV) {
          this.init();
        }
      },
    },
    currentSessionId: {
      immediate: true,
      handler(nV, oV) {
        if (oV || oV === 0) {
          this.clearSessionHandler(oV);
        }
        if (nV || nV === 0) {
          this.getSessionHandler(nV);
        } else {
          this.session = undefined;
        }
      },
    },
    session: {
      handler() {
        this.isStickyButtonsValid = false;
        this.currentSessionValidation = undefined;
        this.errorsCount = {};
      },
      immediate: true,
      deep: true,
    },
    sessionObjects: {
      handler(nV) {
        let hasAnyChanges = false;
        for (const dataSourceChanges of Object.values(nV)) {
          if (hasAnyChanges) break;
          for (const changes of Object.values(dataSourceChanges)) {
            if (changes?.length) hasAnyChanges = true;
            break;
          }
        }
        this.stickyButtons = [
          {
            action: 'agreeZdmEditor',
            translationPath: 'sidebar.zdmEditor.makeChanges',
          },
          {
            action: 'checkZdmEditor',
            translationPath: 'sidebar.zdmEditor.check',
            cancel: true,
            preventGoToLegendCancel: true,
            disabled: !hasAnyChanges,
          },
        ];
      },
      immediate: true,
      deep: true,
    },
  },
  created() {
    this.getSessionHandler = debounce(this.getSessionHandler, 100);
    this.$once('hook:beforeDestroy', () => {
      this.clearSessionHandler();
      this.session = undefined;
      this.stickyButtons = [];
    });
  },
  mounted() {
    this.$root.$off('agreeZdmEditor');
    this.$root.$off('checkZdmEditor');
    this.$root.$on('agreeZdmEditor', this.agreeChanges);
    this.$root.$on('checkZdmEditor', this.checkChanges);
    this.stickyButtons = [
      {
        action: 'agreeZdmEditor',
        translationPath: 'sidebar.zdmEditor.makeChanges',
      },
      {
        action: 'checkZdmEditor',
        translationPath: 'sidebar.zdmEditor.check',
        cancel: true,
        preventGoToLegendCancel: true,
      },
    ];
    this.init();
    this.$watch(
      vm => [vm.isMainDialogVisible, vm.isMainInputDialogVisible],
      nV => {
        const [isMainDialogVisible, isMainInputDialogVisible] = nV;
        if (!isMainDialogVisible && !isMainInputDialogVisible) {
          this.currentAction = undefined;
          this.currentActionData = undefined;
          this.isMainDialogLoading = false;
          this.isMainInputDialogLoading = false;
          this.$refs.dxfFile?.$refs.dropzone?.removeAllFiles();
        }
      }
    );
    this.$watch(
      vm => {
        const { isSessionFetched, session } = vm;
        return { isSessionFetched, session };
      },
      nV => {
        const { isSessionFetched, session } = nV;
        if (isSessionFetched && session) {
          this.isStickyButtonsLoading = false;
          this.isStickyButtonsValid = false;
          this.isStickyButtonsVisible = true;
        }
      }
    );
  },
};
</script>
